import { jumpToStockChart } from '@/utils/stock'

/** 文章正文区域可用宽度（781 容器 - 左右 padding 40*2） */
export const ARTICLE_BODY_WIDTH = 701

export interface ArticleStockItem {
  stockName: string
  stockCode: string
  stockMarket: string
  LastPrice?: number
  PreClosePrice?: number
  rate?: number
  type?: 'up' | 'down' | ''
}

export function extractTextFromHtml(html: string): string {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  let text = ''

  const getTextFromNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent || ''
      return
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = (node as HTMLElement).tagName.toLowerCase()
      if (tag === 'style' || tag === 'em') return
      node.childNodes.forEach(getTextFromNode)
    }
  }

  div.childNodes.forEach(getTextFromNode)
  return text.trim()
}

export function formatArticleRate(rate?: number): string {
  if (rate == null || rate === 0) return '--'
  const pct = (Math.abs(rate) * 100).toFixed(2)
  return rate > 0 ? `+${pct}%` : `-${pct}%`
}

export function parseAttributeStockList(stockList?: string | unknown[]): ArticleStockItem[] {
  if (!stockList) return []
  try {
    const list = typeof stockList === 'string' ? JSON.parse(stockList) : stockList
    if (!Array.isArray(list)) return []
    return list
      .filter((item) => item?.stockName)
      .map((item) => ({
        LastPrice: 0,
        PreClosePrice: 0,
        rate: 0,
        type: '' as const,
        ...item,
      }))
  } catch {
    return []
  }
}

function capOversizedStyle(style: string, maxWidth: number): string {
  let next = style.replace(/width\s*:\s*(\d+(?:\.\d+)?)px/gi, (match, px) =>
    Number(px) > maxWidth ? 'width:100%' : match
  )
  next = next.replace(/white-space\s*:\s*nowrap/gi, 'white-space:normal')
  return next
}

export function formatArticleHtmlContent(content: string, containerWidth?: number): string {
  if (!content) return ''

  let html = content
  const clientWidth = containerWidth || ARTICLE_BODY_WIDTH

  if (!html.match(/>[^<>]+</gim)) {
    html = `<div>${html}</div>`
  }

  html = html.replace(/>[^<>]+</gim, (match) =>
    match
      .replace(/#\|(.+?)\|#/gi, (_m, p1) => `<span class="match-symbol">${String(p1).split('|')[0]}</span>`)
      .replace(/@\|(.+?)\|@/gi, (_m, p1) => `<span class="match-symbol">${String(p1).split('|')[0]}</span>`)
  )

  html = html.replace(/<a\s*[^>]*>(.*?)<\/a>/gi, '$1')
  html = html.replace(/<img[^<>]*?>/gi, (match) =>
    match
      .replace(/width="\d+"/gi, '')
      .replace(/height="\d+"/gi, '')
      .replace(/max-width.*;/gi, '')
      .replace(/\.webp/gi, '.jpeg')
      .replace('>', ' data-type="image" >')
  )
  html = html.replace(/<section[^<>]*?>/gi, (match) =>
    match.replace(/height="\d+"/gi, '').replace(/max-width.*;/gi, '')
  )
  html = html.replace(/\sstyle="([^"]*)"/gi, (_match, style) => {
    const cleaned = capOversizedStyle(String(style), clientWidth)
    return cleaned ? ` style="${cleaned}"` : ''
  })
  html = html.replace(/width="(.+?)"/gi, (match, p1) => (Number(p1) > clientWidth ? 'width="100%"' : match))
  html = html.replace(/[^-]width:(.+?)px/gi, (match, p1) => (Number(p1) > clientWidth ? 'width: 100%' : match))
  html = html.replace(/<[^video][^<>]*?>/gi, (match) => match.replace(/width="\d+"/gi, ''))
  html = html.replace(/class="js_audio_frame">/g, 'class="js_audio_frame" style="display:none"')

  html = html.replace(/<audio([^>]*?)>/gi, (_match, attrs) => {
    let nextAttrs = String(attrs)
    if (/style="/i.test(nextAttrs)) {
      nextAttrs = nextAttrs.replace(/style="/i, 'style="display:block;width:100%;margin:12px 0;')
    } else {
      nextAttrs += ' style="display:block;width:100%;margin:12px 0;"'
    }
    if (!/controls/i.test(nextAttrs)) nextAttrs += ' controls'
    return `<audio${nextAttrs}>`
  })

  html = html.replace(
    /(<section[^>]*class="[^"]*share_audio_context[^"]*"[^>]*?)style="([^"]*)"/gi,
    (_match, before, style) => `${before}style="${style};display:none"`
  )

  return html
}

function formatAudioTime(value: number): string {
  const val = Math.round(value)
  const mins = Math.floor(val / 60).toString().padStart(2, '0')
  const secs = (val % 60).toString().padStart(2, '0')
  return `${mins}:${secs}`
}

export function initShareAudioInContainer(root: HTMLElement | null) {
  if (!root) return
  const audioControllers = Array.from(root.querySelectorAll('.share_audio_context'))
  audioControllers.forEach((audioController) => {
    const shareAudio = audioController.querySelector('.share_audio_length_total') as HTMLElement | null
    const progress = audioController.querySelector('.share_audio_progress_inner') as HTMLElement | null
    const currentTime = audioController.querySelector('.share_audio_length_current') as HTMLElement | null
    const audio = audioController.querySelector('audio') as HTMLAudioElement | null
    if (!audio || !progress || !currentTime) return

    let timer: ReturnType<typeof setInterval> | undefined
    audio.load()
    audio.oncanplay = () => {
      if (shareAudio) shareAudio.innerHTML = formatAudioTime(audio.duration)
    }

    const reset = () => {
      if (timer) clearInterval(timer)
      audio.currentTime = 0
      currentTime.textContent = formatAudioTime(0)
      progress.style.width = '0'
      audioController.classList.remove('share_audio_playing')
    }

    audio.addEventListener('ended', reset)
    audioController.addEventListener('click', () => {
      audioControllers.forEach((item) => item.classList.remove('share_audio_playing'))
      if (audio.paused) {
        audio.play()
        audioController.classList.add('share_audio_playing')
        timer = setInterval(() => {
          currentTime.textContent = formatAudioTime(audio.currentTime)
          progress.style.width = `${(audio.currentTime / audio.duration) * 100}%`
        }, 1000)
      } else {
        audio.pause()
      }
    })
  })
}

export function initArticleVideos(root: HTMLElement | null) {
  if (!root) return
  const videoFrames = Array.from(root.querySelectorAll('.editor-video-content')) as HTMLVideoElement[]
  videoFrames.forEach((videoFrame) => {
    videoFrame.preload = 'auto'
    videoFrame.setAttribute('controlsList', 'nodownload')
  })

  videoFrames.forEach((video) => {
    const ratio = Number((video as HTMLElement & { dataset: DOMStringMap }).dataset.ratio)
    if (!ratio) return
    const width = video.offsetWidth
    video.style.cssText = `height: ${width * ratio}px !important;width: ${width}px !important;`
  })
}

export function bindArticleQuoteClick(root: HTMLElement | null) {
  if (!root) return () => {}
  const quotes = Array.from(root.querySelectorAll('.quote'))
  const handlers: Array<{ el: Element; fn: () => void }> = []

  quotes.forEach((item) => {
    const quoteId = item.getAttribute('data-quote') || item.getAttribute('data-id') || ''
    if (!quoteId) return
    const ids = quoteId.split('|')
    if (ids.length < 3) return
    const stockMarket = ids[1]
    const stockCode = ids[2]
    const fn = () => jumpToStockChart(`${stockCode}.${stockMarket}`)
    item.addEventListener('click', fn)
    handlers.push({ el: item, fn })
  })

  return () => {
    handlers.forEach(({ el, fn }) => el.removeEventListener('click', fn))
  }
}

export function bindArticleImageClick(root: HTMLElement | null) {
  if (!root) return () => {}
  const images = Array.from(root.querySelectorAll('img[data-type="image"], .headline-news-content img'))
  const handlers: Array<{ el: Element; fn: () => void }> = []

  images.forEach((img) => {
    const fn = () => {
      const src = (img as HTMLImageElement).src
      if (src) window.open(src)
    }
    img.addEventListener('click', fn)
    ;(img as HTMLElement).style.cursor = 'pointer'
    handlers.push({ el: img, fn })
  })

  return () => handlers.forEach(({ el, fn }) => el.removeEventListener('click', fn))
}

export function pauseOtherMedia(root: HTMLElement | null) {
  if (!root) return
  const videos = Array.from(root.querySelectorAll('video'))
  const audios = Array.from(root.querySelectorAll('audio'))

  videos.forEach((video, index) => {
    video.addEventListener('play', () => {
      videos.forEach((item, idx) => {
        if (idx !== index) item.pause()
      })
      audios.forEach((item) => item.pause())
    })
  })

  audios.forEach((audio, index) => {
    audio.addEventListener('play', () => {
      audios.forEach((item, idx) => {
        if (idx !== index) item.pause()
      })
      videos.forEach((item) => item.pause())
    })
  })
}

export function updateArticleStockRates(
  list: ArticleStockItem[],
  val: number,
  code: string,
  type: 0 | 1,
  market: string
): ArticleStockItem[] {
  const upperMarket = market.toUpperCase()
  return list.map((item) => {
    const matched =
      item.stockCode === code && item.stockMarket.toUpperCase() === upperMarket
    if (!matched) return item
    const next = { ...item }
    if (type === 0) next.PreClosePrice = val
    else next.LastPrice = val
    if (next.LastPrice && next.PreClosePrice) {
      let rate = Math.abs(next.LastPrice - next.PreClosePrice) / next.PreClosePrice
      const stockType = next.LastPrice > next.PreClosePrice ? 'up' : next.LastPrice < next.PreClosePrice ? 'down' : ''
      rate = next.LastPrice > next.PreClosePrice ? rate : -rate
      if (next.LastPrice === next.PreClosePrice) {
        next.type = ''
        next.rate = 0
      } else {
        next.type = stockType as 'up' | 'down' | ''
        next.rate = +rate.toFixed(4)
      }
    }
    return next
  })
}
